import type {
  AcaoAvaliacaoDef,
  DadoClinicoAcao,
  DefRotaExtracao,
  EtapaAvaliacao,
  FaseAtendimento,
  IdAcaoAvaliacao,
  LetraXabcde,
  ModoAcaoAvaliacao,
} from './tipos'

/** Rótulo do botão de registro — o professor nunca confunde pergunta, exame e aferição. */
export const ROTULOS_MODO_ACAO: Readonly<Record<ModoAcaoAvaliacao, string>> = {
  pergunta: 'Perguntar',
  exame: 'Examinar',
  afericao: 'Aferir',
}

export const ETAPAS_AVALIACAO_ORDENADAS: readonly { id: EtapaAvaliacao; rotulo: string; descricao: string }[] = [
  { id: 'impressao_geral', rotulo: 'Impressão Geral', descricao: 'Aparência, queixa espontânea e cena inicial' },
  { id: 'X', rotulo: 'Hemorragia', descricao: 'Controle imediato de sangramento exsanguinante externo' },
  { id: 'A', rotulo: 'Via Aérea', descricao: 'Permeabilidade da via aérea e alinhamento cervical' },
  { id: 'B', rotulo: 'Respiração', descricao: 'Frequência, ritmo, expansão torácica e ausculta' },
  { id: 'C', rotulo: 'Circulação', descricao: 'Pulsos, perfusão capilar, pressão arterial e pele' },
  { id: 'D', rotulo: 'Neurológico', descricao: 'Escala de Glasgow, nível AVDI e pupilas' },
  { id: 'E', rotulo: 'Exposição', descricao: 'Exposição controlada e prevenção da hipotermia' },
  { id: 'sinais', rotulo: 'Sinais Vitais', descricao: 'Oximetria, glicemia, temperatura e dor' },
  { id: 'sample', rotulo: 'Anamnese SAMPLE', descricao: 'Histórico direcionado à vítima ou testemunhas' },
  { id: 'secundaria', rotulo: 'Exame Céfalo-Caudal', descricao: 'Inspeção e palpação estruturada por regiões' },
]

export const ACOES_AVALIACAO: readonly AcaoAvaliacaoDef[] = [
  // Impressão Geral
  {
    id: 'chegada_queixa_aparencia',
    rotulo: 'Aparência e queixa inicial',
    etapa: 'impressao_geral',
    modo: 'exame',
    descricao: 'Observar a postura geral, coloração visível e primeira manifestação da vítima.',
    regiaoPadrao: 'corpoTodo',
  },
  {
    id: 'chegada_instrucao_ator',
    rotulo: 'Comportamento do ator / resposta inicial',
    etapa: 'impressao_geral',
    modo: 'exame',
    descricao: 'Como o paciente se comporta ao ser abordado pela primeira vez na cena.',
    regiaoPadrao: 'cabeca',
  },

  // X - Hemorragia Exsanguinante
  {
    id: 'x_hemorragia_externa',
    rotulo: 'Inspecionar hemorragia exsanguinante',
    etapa: 'X',
    modo: 'exame',
    descricao: 'Verificar se há sangramento maciço em jato ou poça com necessidade de torniquete imediato.',
    regiaoPadrao: 'corpoTodo',
  },

  // A - Vias Aéreas e Colar Cervical
  {
    id: 'a_permeabilidade_via_aerea',
    rotulo: 'Avaliar via aérea e fala',
    etapa: 'A',
    modo: 'exame',
    descricao: 'Observar se a vítima fala claramente, se há roncos, estridor ou corpos estranhos.',
    regiaoPadrao: 'cabeca',
  },

  // B - Respiração
  {
    id: 'b_frequencia_respiratoria',
    rotulo: 'Contar frequência respiratória (FR)',
    etapa: 'B',
    modo: 'afericao',
    descricao: 'Determinar a frequência por minuto e se a respiração é superficial ou laboriosa.',
    regiaoPadrao: 'torax',
  },
  {
    id: 'b_ausculta_pulmonar',
    rotulo: 'Auscultar campos pulmonares',
    etapa: 'B',
    modo: 'exame',
    descricao: 'Auscultar ápices e bases bilateralmente verificando presença de murmúrios e ruídos adventícios.',
    regiaoPadrao: 'torax',
  },
  {
    id: 'b_expansao_toracica',
    rotulo: 'Inspecionar dinâmica e simetria do tórax',
    etapa: 'B',
    modo: 'exame',
    descricao: 'Observar expansão bilateral, assimetria, respiração paradoxal ou ferimento aspirativo.',
    regiaoPadrao: 'torax',
  },

  // C - Circulação
  {
    id: 'c_pulso_radial',
    rotulo: 'Palpar pulso radial geral',
    etapa: 'C',
    modo: 'afericao',
    descricao: 'Frequência, ritmo e qualidade do pulso periférico mais acessível.',
    regiaoPadrao: 'membroSuperiorDireito',
  },
  {
    id: 'c_pulsos_especificos',
    rotulo: 'Avaliar pulsos específicos / simetria',
    etapa: 'C',
    modo: 'exame',
    descricao: 'Comparar pulsos radiais D e E ou verificar pulso pedioso no membro afetado.',
    regiaoPadrao: 'membroSuperiorEsquerdo',
  },
  {
    id: 'c_perfusao_periferica',
    rotulo: 'Testar tempo de enchimento capilar',
    etapa: 'C',
    modo: 'afericao',
    descricao: 'Pressionar o leito ungueal e verificar retorno em segundos (< 2 s ou prolongado).',
    regiaoPadrao: 'membroSuperiorDireito',
  },
  {
    id: 'c_pressao_arterial',
    rotulo: 'Aferir pressão arterial (PA)',
    etapa: 'C',
    modo: 'afericao',
    descricao: 'Aferir a PA por método auscultatório ou palpatório com esfigmomanômetro.',
    regiaoPadrao: 'membroSuperiorDireito',
  },
  {
    id: 'c_pele_temperatura_umidade',
    rotulo: 'Inspecionar pele (cor, temperatura, umidade)',
    etapa: 'C',
    modo: 'exame',
    descricao: 'Verificar palidez, sudorese, cianose, frialdade ou aquecimento.',
    regiaoPadrao: 'corpoTodo',
  },
  {
    id: 'c_estase_jugular',
    rotulo: 'Inspecionar turgência jugular',
    etapa: 'C',
    modo: 'exame',
    descricao: 'Avaliar ingurgitamento das veias jugulares em 45° ou na posição encontrada.',
    regiaoPadrao: 'cabeca',
  },

  // D - Neurológico
  {
    id: 'd_escala_glasgow',
    rotulo: 'Avaliar Escala de Coma de Glasgow',
    etapa: 'D',
    modo: 'afericao',
    descricao: 'Avaliar abertura ocular (1-4), resposta verbal (1-5) e motora (1-6).',
    regiaoPadrao: 'cabeca',
  },
  {
    id: 'd_nivel_avdi',
    rotulo: 'Classificar nível AVDI',
    etapa: 'D',
    modo: 'exame',
    descricao: 'Alerta, resposta a estímulo Verbal, resposta à Dor ou Inconsciente.',
    regiaoPadrao: 'cabeca',
  },
  {
    id: 'd_pupilas',
    rotulo: 'Examinar pupilas (tamanho e fotorreatividade)',
    etapa: 'D',
    modo: 'exame',
    descricao: 'Isocóricas, anisocóricas, midriáticas, mióticas e reação à luz.',
    regiaoPadrao: 'cabeca',
  },

  // E - Exposição
  {
    id: 'e_exposicao_dorso',
    rotulo: 'Exposição corporal e controle térmico',
    etapa: 'E',
    modo: 'exame',
    descricao: 'Remover vestes necessárias, inspecionar abdômen/dorso e aquecer para evitar hipotermia.',
    regiaoPadrao: 'corpoTodo',
  },

  // Sinais Vitais
  {
    id: 'sinais_spo2',
    rotulo: 'Oximetria de pulso (SpO₂)',
    etapa: 'sinais',
    modo: 'afericao',
    descricao: 'Percentual de saturação periférica de oxigênio pelo oxímetro de pulso.',
    regiaoPadrao: 'membroSuperiorDireito',
  },
  {
    id: 'sinais_glicemia',
    rotulo: 'Glicemia capilar',
    etapa: 'sinais',
    modo: 'afericao',
    descricao: 'Medição de glicose no sangue em mg/dL com glicosímetro.',
    regiaoPadrao: 'membroSuperiorDireito',
  },
  {
    id: 'sinais_temperatura',
    rotulo: 'Temperatura corporal',
    etapa: 'sinais',
    modo: 'afericao',
    descricao: 'Medição da temperatura axilar ou timpânica em °C.',
    regiaoPadrao: 'cabeca',
  },
  {
    id: 'sinais_escala_dor',
    rotulo: 'Intensidade da dor (0 a 10)',
    etapa: 'sinais',
    modo: 'afericao',
    descricao: 'Quantificação da dor relatada pela vítima na escala numérica verbal.',
    regiaoPadrao: 'corpoTodo',
  },

  // SAMPLE
  {
    id: 'sample_sintomas',
    rotulo: 'S — Sintomas e queixas',
    etapa: 'sample',
    modo: 'pergunta',
    descricao: 'O que a vítima sente: dor, dispneia, tontura, dormência, pressão.',
    regiaoPadrao: 'cabeca',
  },
  {
    id: 'sample_alergias',
    rotulo: 'A — Alergias',
    etapa: 'sample',
    modo: 'pergunta',
    descricao: 'Alergias conhecidas a medicamentos, alimentos ou látex.',
    regiaoPadrao: 'cabeca',
  },
  {
    id: 'sample_medicamentos',
    rotulo: 'M — Medicamentos em uso',
    etapa: 'sample',
    modo: 'pergunta',
    descricao: 'Remédios contínuos, anticoagulantes, anti-hipertensivos ou insulina.',
    regiaoPadrao: 'cabeca',
  },
  {
    id: 'sample_passado_medico',
    rotulo: 'P — Passado médico e cirurgias',
    etapa: 'sample',
    modo: 'pergunta',
    descricao: 'Histórico de doenças crônicas, cirurgias ou condições prévias.',
    regiaoPadrao: 'cabeca',
  },
  {
    id: 'sample_liquidos_alimentos',
    rotulo: 'L — Líquidos e última refeição',
    etapa: 'sample',
    modo: 'pergunta',
    descricao: 'Horário e tipo da última refeição sólida ou ingestão de líquidos.',
    regiaoPadrao: 'cabeca',
  },
  {
    id: 'sample_evento_trauma',
    rotulo: 'E — Eventos do acidente',
    etapa: 'sample',
    modo: 'pergunta',
    descricao: 'O que a vítima relata sobre como a colisão ou impacto ocorreu.',
    regiaoPadrao: 'cabeca',
  },

  // Avaliação Secundária Céfalo-Caudal
  {
    id: 'sec_cabeca_pescoco',
    rotulo: 'Exame de cabeça, face e pescoço',
    etapa: 'secundaria',
    modo: 'exame',
    descricao: 'Hematomas, deformidades, sinal de Battle, olhos de guaxinim, traqueia e jugulares.',
    regiaoPadrao: 'cabeca',
  },
  {
    id: 'sec_torax_abdomen_pelve',
    rotulo: 'Exame de tórax, abdômen e pelve',
    etapa: 'secundaria',
    modo: 'exame',
    descricao: 'Dor a palpação, crepitação, distensão, rigidez em tábua, estabilidade pélvica.',
    regiaoPadrao: 'torax',
  },
  {
    id: 'sec_membros_dorso',
    rotulo: 'Exame de membros (PMS) e dorso',
    etapa: 'secundaria',
    modo: 'exame',
    descricao: 'Pulso, motricidade, sensibilidade (PMS) distal por membro, deformidades e dorso.',
    regiaoPadrao: 'membroInferiorEsquerdo',
  },
]

export const ACOES_POR_ID: Readonly<Record<IdAcaoAvaliacao, AcaoAvaliacaoDef>> = Object.freeze(
  ACOES_AVALIACAO.reduce((acc, acao) => {
    acc[acao.id] = acao
    return acc
  }, {} as Record<IdAcaoAvaliacao, AcaoAvaliacaoDef>),
)

export const ROTAS_EXTRACAO: readonly DefRotaExtracao[] = [
  {
    id: 'rota_rapida',
    rotulo: 'Extração Rápida / Emergencial',
    descricao: 'Manobra de emergência manual (chave de Rauteck ou puxada rápida) indicada para parada iminente, choque descompensado profundo ou risco grave da cena.',
  },
  {
    id: 'rota_lateral_porta',
    rotulo: 'Retirada Lateral pela Porta',
    descricao: 'Retirada com ângulo de saída através de porta aberta, desencaixada ou removida, mantendo alinhamento longitudinal na prancha.',
  },
  {
    id: 'rota_lateral_b',
    rotulo: 'Retirada Lateral Ampla (com Coluna B)',
    descricao: 'Abertura total da lateral com remoção das portas e afastamento/corte da coluna B (terceira porta), permitindo giro mínimo do corpo.',
  },
  {
    id: 'rota_longitudinal_traseira',
    rotulo: 'Retirada Longitudinal (Porta-malas / Ré)',
    descricao: 'Saída da vítima pelo porta-malas através de rebatimento dos bancos traseiros ou remoção da tampa, com alinhamento reto da coluna.',
  },
  {
    id: 'rota_vertical_teto',
    rotulo: 'Retirada Vertical (Teto Removido/Rebatido)',
    descricao: 'Retirada para cima após rebatimento frontal/traseiro ou remoção total do teto com prancha rígida e ângulo reto de extração.',
  },
  {
    id: 'autoextracao_assistida',
    rotulo: 'Autoextração Assistida / Guiada',
    descricao: 'Orientação verbal e suporte para vítima consciente, hemodinamicamente estável e sem aprisionamento físico nem dor axial impeditiva.',
  },
]


/**
 * Roteiro de aula: uma pergunta-gatilho por fase, mostrada ao instrutor e no
 * projetor. O instrutor só revela o achado depois que a turma responder em
 * voz alta — a pergunta nunca aparece já respondida.
 */
export const FASES_ROTEIRO: Readonly<Record<FaseAtendimento, { rotulo: string; pergunta: string }>> = {
  abordagem: {
    rotulo: 'Abordagem',
    pergunta: 'O que aconteceu? Como abordar, orientar e avaliar a vítima pelo lado de fora?',
  },
  intervencao: {
    rotulo: 'Intervenção',
    pergunta: 'O acesso está liberado? Quais procedimentos internos e achados do XABCDE orientam a classificação e a comunicação à equipe?',
  },
  extracao: {
    rotulo: 'Extração',
    pergunta: 'Considerando a avaliação, o encarceramento e os acessos, qual técnica, rota e ângulo de retirada?',
  },
}

/** Sinal exibido no pequeno monitor do painel — sigla curta, achado que o alimenta. */
export interface SlotMonitorSinal {
  readonly sigla: string
  readonly rotulo: string
  readonly idAcao: IdAcaoAvaliacao
}

export const SLOTS_MONITOR: readonly SlotMonitorSinal[] = [
  { sigla: 'FR', rotulo: 'Frequência respiratória', idAcao: 'b_frequencia_respiratoria' },
  { sigla: 'FC', rotulo: 'Frequência cardíaca / pulso', idAcao: 'c_pulso_radial' },
  { sigla: 'PA', rotulo: 'Pressão arterial', idAcao: 'c_pressao_arterial' },
  { sigla: 'SpO₂', rotulo: 'Saturação de oxigênio', idAcao: 'sinais_spo2' },
  { sigla: 'TEC', rotulo: 'Tempo de enchimento capilar', idAcao: 'c_perfusao_periferica' },
  { sigla: 'Glasgow', rotulo: 'Escala de Coma de Glasgow', idAcao: 'd_escala_glasgow' },
  { sigla: 'Temp', rotulo: 'Temperatura corporal', idAcao: 'sinais_temperatura' },
  { sigla: 'Dor', rotulo: 'Escala de dor', idAcao: 'sinais_escala_dor' },
]

/**
 * Tipos de intervenção (ver `PainelAtendimentoVitima`/`FormIntervencao`) sugeridos por letra,
 * apenas para atalho de navegação — o instrutor decide quais fazem sentido no caso.
 */
export const INTERVENCOES_POR_LETRA: Readonly<Partial<Record<LetraXabcde, readonly string[]>>> = {
  X: ['torniquete'],
  A: ['colar_cervical'],
  B: ['oxigenioterapia'],
  C: ['torniquete', 'manta_termica'],
  D: [],
  E: ['manta_termica', 'imobilizacao_membro'],
}

/**
 * Leitura curta para o monitor de sinais: usa `leitura`, quando cadastrada na ficha,
 * ou o trecho antes da primeira vírgula de `valor` como aproximação.
 */
export function leituraCurta(achado: DadoClinicoAcao): string {
  if (achado.leitura) return achado.leitura
  const [primeiroTrecho] = achado.valor.split(',')
  return primeiroTrecho.trim()
}
